import { links } from '@/constants/links'

export interface QRCodeResult {
  type: 'user-profile' | 'wallet-address' | 'payment-request' | 'unknown'
  data: {
    userId?: string
    walletAddress?: string
    paymentData?: {
      id: string
      name: string
      username?: string
      avatar?: string
      type: 'send' | 'request'
      currencyValue?: string
      fiatValue?: string
      fiatCode?: string
      phoneNumber?: string
    }
    originalUrl?: string
  }
}

/**
 * Parse QR code result and determine the type and extract relevant data
 * Based on React Native implementation logic
 */
export const parseQRCode = (scanningResult: string): QRCodeResult => {
  const trimmedResult = scanningResult.trim()

  // Check if it's a user profile link
  const userProfileBasePath = `${links.universalLinkDomain}/user-profile/`
  if (trimmedResult.includes(userProfileBasePath)) {
    const userId = trimmedResult.substring(
      trimmedResult.indexOf(userProfileBasePath) + userProfileBasePath.length,
    )

    return {
      type: 'user-profile',
      data: {
        userId: userId.split('?')[0], // Remove query params if any
        originalUrl: trimmedResult,
      },
    }
  }

  // Check if it's a payment request link
  const paymentRequestBasePath = `${links.universalLinkDomain}/send-request/recipient`
  if (trimmedResult.includes(paymentRequestBasePath)) {
    try {
      const url = new URL(trimmedResult)
      const searchParams = url.searchParams

      return {
        type: 'payment-request',
        data: {
          paymentData: {
            id: searchParams.get('id') || '',
            name: searchParams.get('name') || '',
            username: searchParams.get('username') || undefined,
            avatar: searchParams.get('avatar') || undefined,
            type: (searchParams.get('type') as 'send' | 'request') || 'send',
            currencyValue: searchParams.get('currencyValue') || undefined,
            fiatValue: searchParams.get('fiatValue') || undefined,
            fiatCode: searchParams.get('fiatCode') || undefined,
            phoneNumber: searchParams.get('phoneNumber') || undefined,
          },
          originalUrl: trimmedResult,
        },
      }
    } catch (error) {
      console.error('Failed to parse payment request URL:', error)
    }
  }

  // Check if it looks like a wallet address (basic validation)
  if (isWalletAddress(trimmedResult)) {
    return {
      type: 'wallet-address',
      data: {
        walletAddress: trimmedResult,
        originalUrl: trimmedResult,
      },
    }
  }

  // Unknown QR code type
  return {
    type: 'unknown',
    data: {
      originalUrl: trimmedResult,
    },
  }
}

/**
 * Basic wallet address validation
 * This is a simplified version - in production you might want more sophisticated validation
 */
const isWalletAddress = (address: string): boolean => {
  // Bitcoin address patterns
  if (/^[13][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(address)) return true // Legacy
  if (/^bc1[a-z0-9]{39,59}$/.test(address)) return true // Bech32

  // Ethereum address pattern
  if (/^0x[a-fA-F0-9]{40}$/.test(address)) return true

  // Add more patterns as needed for other cryptocurrencies

  return false
}

/**
 * Generate user profile QR code URL
 */
export const generateUserProfileQRUrl = (userId: string): string => {
  return `${links.universalLinkDomain}/user-profile/${userId}`
}

/**
 * Generate payment request QR code URL
 */
export const generatePaymentRequestQRUrl = (params: {
  id: string
  currencyValue: string
  currencyCode: string
  fiatCode: string
  fiatValue: string
}): string => {
  const url = new URL(`${links.universalLinkDomain}/send-request/recipient`)
  Object.entries(params).forEach(([key, value]) => {
    if (value) url.searchParams.set(key, value)
  })
  return url.toString()
}
